import {escapeRegex} from 'directory/widget/regex.js';
import events from 'directory/widget/events.js';
import http from 'directory/widget/http.js';
import ko from 'knockout';

const autocompleteTemplate = `
<span class="check">
    <input
        type="text"
        autocomplete="off"
        data-bind="
            attr: {
                name: name,
                placeholder: placeholder,
                autofocus: autofocus,
            },
            textInput: query,
            event: {
                keydown: keydown,
                keypress: keypress,
            },
            css: inputClasses()">
    <button type="reset"
        data-bind="
            event: {
                click: resetInput,
            }"
    >Clear</button>
</span>
<div class="autocomplete-results-holder hide" data-bind="css: {hide: results() && !results().length}">
    <!-- ko if: results -->
        <ul class="list-unstyled results" data-bind="foreach: results">
            <li
                data-bind="
                    css: {
                        selected: $index() == $parent.selectedIndex()
                    },
                    click: $parent.select
            ">
                <!-- ko if: $parent.getIcon -->
                    <i data-bind="css: $parent.getIcon($data)"></i>&nbsp;
                <!-- /ko -->
                <span data-bind="html: $parent.highlight($data)"></span>
            </li>
        </ul>
    <!-- /ko -->
    <!-- ko ifnot: results -->
        <ul class="list-unstyled results no-results">
            <li>No results</li>
        </ul>
    <!-- /ko -->
</div>`;

class AutoCompleteModel {
    constructor(params, elem) {
        this.params = params;
        this.query = ko.observable(params.query || '');
        this.results = ko.observableArray();
        this.selectedIndex = ko.observable(-1);

        this.url = ko.unwrap(params.url);
        this.param = ko.unwrap(params.param) || 'q';
        this.name = params.name;
        this.healthFundId = params.healthFundId;
        this.placeholder = ko.unwrap(params.placeholder) || '';
        this.autofocus = ko.unwrap(params.autofocus) || false;
        this.showNoResults = params.showNoResults || true;

        this.curQuery = null;
        this.getIcon = params.getIcon;

        if (params.initial) {
            let init = params.initial;
            if (typeof init === 'function') {
                init = init();
            }
            if (init) {
                init = params.format(init);
                this.query(init);
            }
        }

        // Set up rate limit here so that it won't trigger an update from above
        // init change after subscriber below is set up
        this.query.extend({
            rateLimit: {
                timeout: 100,
                method: 'notifyWhenChangesStop',
            },
        });

        this.query.subscribe(async (value) => {
            // Should we skip searching this time?
            if (this.skipSearch) {
                this.skipSearch = false;
                return;
            }

            if (this.params.onQueryUpdate) {
                this.params.onQueryUpdate(value);
            }

            if (value === '') {
                this.results([]);
                return;
            }

            if (this.curQuery) {
                this.curQuery.abort();
            }
            this.curQuery = new AbortController();

            const data = {
                [this.param]: value,
                health_fund_id: this.healthFundId,
            };

            let url = this.url;
            if (typeof this.url === 'function') {
                url = url();
            }

            try {
                const response = await http.get({
                    url,
                    data,
                    signal: this.curQuery.signal,
                });

                if (params.getResults) {
                    this.results(params.getResults(response, value));
                } else {
                    if (response.results) {
                        this.results(response.results);
                    } else {
                        if (response.length) {
                            this.results(response);
                        } else if (this.showNoResults) {
                            this.results(null);
                        } else {
                            this.results([]);
                        }
                    }
                }
                this.curQuery = null;
            } catch (ex) {
                this.results([]);
                this.curQuery = null;
            }
        });

        this.pattern = ko.pureComputed(() => {
            const pieces = this.query()
                .split(/\s+/)
                .map((piece) => escapeRegex(piece));
            return new RegExp(`\\b(${pieces.join('|')})`, 'gi');
        });

        // Let clicking outside of options disable dropdown
        events.listen(document.body, 'click', (e) => {
            if (!elem.contains(e.target)) {
                setTimeout(() => this.results([]));
            }
        });
        // TODO dispose above event listener on element removal - low priority

        // Handle scroll state of the search results container
        this.selectedIndex.subscribe((val) => {
            const results = elem.getElementsByClassName('results')[0];
            if (val >= 0) {
                const result = results.getElementsByTagName('li')[val];
                const resultOffset = result.offsetTop + result.offsetHeight;
                const resultsOffset = results.offsetHeight + results.scrollTop;
                if (resultOffset > resultsOffset) {
                    // handle scrolling down
                    results.scrollTop += resultOffset - resultsOffset;
                } else if (result.offsetTop < results.scrollTop) {
                    // handle scrolling up
                    results.scrollTop = result.offsetTop;
                }
            } else {
                results.scrollTop = 0;
            }
        });

        this.select = this.select.bind(this);
    }

    inputClasses() {
        let classes = 'form-control';
        if (this.params.class) {
            classes += ` ${ko.unwrap(this.params.class)}`;
        }
        return classes;
    }

    highlight(result) {
        const text = this.params.format(result);
        // Allow html in formatted text, don't break it when highlighting
        const span = document.createElement('span');
        span.innerHTML = text;
        let out = '';
        for (const child of span.childNodes) {
            if (child.nodeType === document.TEXT_NODE) {
                out += child.nodeValue.replace(
                    this.pattern(),
                    '<strong>$1</strong>',
                );
            } else {
                // This is simple, but won't work if we have nested text nodes
                // eg some text <i>italic</i> other text
                out += child.outerHTML;
            }
        }
        return out;
    }

    select(result) {
        const format = this.params.formatResult || this.params.format;
        const text = format(result);
        this.results([]);
        this.selectedIndex(-1);
        if (text !== this.query()) {
            this.skipSearch = true; // Don't do new search because we set value
            this.query(text);
        }
        // Fill value into hidden input indicated by id
        if (this.params.input) {
            const input = document.getElementById(
                ko.unwrap(this.params.input),
            );
            const value = this.params.value ? this.params.value(result) : text;
            input.value = value;
        }
        if (this.params.afterSelect) {
            this.params.afterSelect(result);
        }
    }

    keydown(_, event) {
        const key = event.key || event.keyCode || event.which;
        if (!this.results() || !this.results().length) {
            return true;
        }
        if (key === 38 || key === 'Up' || key === 'ArrowUp') {
            this.selectedIndex(Math.max(0, this.selectedIndex() - 1));
            return;
        } else if (key === 40 || key === 'Down' || key === 'ArrowDown') {
            this.selectedIndex(
                Math.min(this.results().length - 1, this.selectedIndex() + 1),
            );
            return;
        } else if (key === 27 || key === 'Escape') {
            this.results([]);
            this.selectedIndex(-1);
            return;
        }
        return true;
    }

    keypress(_, event) {
        const key = event.key || event.keyCode || event.which;
        if (key === 13 || key === 'Enter') {
            // If results box is open and a result is highlighted intercept
            if (!this.results()) {
                return;
            }
            if (this.results().length && this.selectedIndex() > -1) {
                this.select(this.results()[this.selectedIndex()]);
                return;
            }
        }
        return true; // Continue with event
    }

    resetInput() {
        if (this.params.afterReset) {
            this.params.afterReset();
        }
    }
}

ko.components.register('auto-complete', {
    viewModel: {
        createViewModel(params, info) {
            return new AutoCompleteModel(params, info.element);
        },
    },
    template: autocompleteTemplate,
});
